import React from 'react';
import {
  ActionList,
  ActionListItem,
  Alert,
  Box,
  Button,
  DownloadIcon,
  Dropdown,
  DropdownOverlay,
  FileUpload,
  Heading,
  Link,
  RayIcon,
  SelectInput,
  Text,
  TextArea,
  TrashIcon,
  useToast,
} from '@razorpay/blade/components';
import type { BladeFile, BladeFileList } from '@razorpay/blade/components';
import { useQuery, useMutation } from 'react-query';
import { LOAN_CATEGORY_OTHER_REASONS } from '../constants';
import api from 'api';
import { AppError, getAppErrorDetails } from 'utils/AppError';
import { useFormWithProvider } from 'forms';
import { useWizardInfo } from 'components/WizardViews/providers/WizardInfoProvider';
import {
  FormWrapper,
  StepActionsWrapper,
  StepContentWrapper,
} from 'components/WizardViews/components';
import { MimeType } from 'components/ui/FileUpload/constants';
import { CreateLoanRequestFormData } from './types';
import { EMPLOYEE_LOAN_CATEGORIES_KEY } from './constants';
import type { PolicySummaryItem } from 'components/Settings/EmployeeLoansSettings/types';

const PolicySummary = ({
  policyFileId,
  policySummary,
}: {
  policyFileId: string;
  policySummary?: PolicySummaryItem[] | null;
}) => {
  if (!policySummary || policySummary.length === 0) return null;

  return (
    <Box
      width="380px"
      height="100%"
      padding="spacing.7"
      borderLeftWidth="thin"
      borderColor="surface.border.gray.subtle"
      display="flex"
      flexDirection="column"
      gap="spacing.6"
      flexShrink={0}
      backgroundColor="surface.background.gray.moderate">
      <Box display="flex" alignItems="center" gap="spacing.3">
        <RayIcon color="surface.icon.primary.normal" />
        <Text size="small" weight="semibold" variant="body" color="surface.text.gray.normal">
          Policy Summary
        </Text>
      </Box>
      <Box display="flex" flexDirection="column" flex="1" overflowY="auto">
        {policySummary.map((item, idx) => (
          <Box key={idx}>
            <Box paddingY="spacing.3">
              <Text size="small" weight="semibold" color="surface.text.gray.subtle">
                {item.label}
              </Text>
              <Text size="small" color="surface.text.gray.muted" marginTop="spacing.1">
                {item.value}
              </Text>
            </Box>
            {idx < policySummary.length - 1 && (
              <Box borderBottomWidth="thin" borderColor="surface.border.gray.subtle" />
            )}
          </Box>
        ))}
      </Box>
      {policyFileId && (
        <Button
          variant="tertiary"
          size="small"
          icon={DownloadIcon}
          isFullWidth
          onClick={() => {
            window.location.href = `/v2/api/loans/download/${policyFileId}`;
          }}>
          Download Policy Document
        </Button>
      )}
    </Box>
  );
};

const RequestDetails = () => {
  const { updateStepStatus, setCurrentStep, closeWizard } = useWizardInfo();
  const { formData, setFormData, setFieldValue } = useFormWithProvider<CreateLoanRequestFormData>();

  const { data: categoriesData } = useQuery({
    queryKey: [EMPLOYEE_LOAN_CATEGORIES_KEY],
    queryFn: api.loans.listEmployeeLoanCategories,
  });

  const { show: showToast } = useToast();

  const uploadDocumentMutation = useMutation({
    mutationFn: (file: File) => api.loans.uploadLoanDocument(file),
    onSuccess: (res) => setFieldValue('documentFileId', res.file_id),
  });

  const downloadDocumentMutation = useMutation(
    (fileId: string) => api.loans.downloadLoanDocument(fileId),
    {
      onSuccess: (res) => {
        window.open(res.signed_url, '_blank');
      },
      onError: (error: typeof AppError) => {
        const { messageForUser } = getAppErrorDetails(error);
        showToast({
          content: messageForUser || 'Failed to open document. Please try again.',
          color: 'negative',
          duration: 5000,
        });
      },
    },
  );

  const hasExistingFile = !!formData.documentFileId && (formData.documentFile ?? []).length === 0;

  const categories = categoriesData?.data ?? [];
  const categoryOptions = categories.map((c) => ({ label: c.name, value: String(c.id) }));

  const hasFileSelected = (formData.documentFile ?? []).length > 0;
  const uploadFailed = uploadDocumentMutation.isError;

  const selectedCategory = categories.find((c) => c.id === formData.loanCategoryId);
  const isDescriptionRequired =
    !!selectedCategory?.is_default &&
    selectedCategory.name.trim().toLowerCase() === LOAN_CATEGORY_OTHER_REASONS.toLowerCase();

  const isNextDisabled =
    !formData.loanCategoryId ||
    (isDescriptionRequired && !formData.description?.trim()) ||
    (formData.isDocumentRequired &&
      (!formData.documentFileId || uploadDocumentMutation.isLoading)) ||
    (!formData.isDocumentRequired && uploadDocumentMutation.isLoading);

  const handleNext = () => {
    updateStepStatus(0, 'success');
    setCurrentStep(1);
  };

  const handleCategoryChange = ({ values }: { values: string[] }) => {
    const id = Number(values[0]);
    const cat = categories.find((c) => c.id === id);
    uploadDocumentMutation.reset();
    setFormData({
      ...formData,
      loanCategoryId: id || null,
      categoryName: cat?.name ?? '',
      isDocumentRequired: cat?.is_document_required ?? false,
      policyFileId: cat?.policy_file_id ?? null,
      policySummary: cat?.policy_summary ?? null,
      documentFile: [],
      documentFileId: null,
    });
  };

  const handleFileChange = ({ fileList }: { fileList: BladeFileList }) => {
    setFieldValue('documentFile', fileList);
    const file = fileList[0] as BladeFile;
    if (file) uploadDocumentMutation.mutate(file);
  };

  const handleFileRemove = ({ file }: { file: BladeFile }) => {
    setFieldValue(
      'documentFile',
      (formData.documentFile ?? []).filter((f) => f.id !== file.id),
    );
    setFieldValue('documentFileId', null);
  };

  return (
    <Box display="flex" flex={1} overflow="hidden">
      <FormWrapper
        onSubmit={(e) => {
          e.preventDefault();
          handleNext();
        }}
        style={{ flex: 1, minWidth: 0 }}>
        <StepContentWrapper noBgIllustration width="462px">
          <Box display="flex" flexDirection="column" gap="spacing.8">
            <Box>
              <Heading size="large" weight="semibold" color="surface.text.gray.normal">
                Request Details
              </Heading>
              <Text
                size="medium"
                variant="body"
                weight="regular"
                color="surface.text.gray.muted"
                marginTop="spacing.2">
                Provide a reason for this request. Please follow the policy guidelines for faster
                approval.
              </Text>
            </Box>

            <Dropdown selectionType="single">
              <SelectInput
                label="What do you need funds for?"
                placeholder="Select a category"
                necessityIndicator="required"
                value={formData.loanCategoryId ? String(formData.loanCategoryId) : undefined}
                onChange={handleCategoryChange}
              />
              <DropdownOverlay>
                <ActionList>
                  {categoryOptions.map((opt) => (
                    <ActionListItem key={opt.value} title={opt.label} value={opt.value} />
                  ))}
                </ActionList>
              </DropdownOverlay>
            </Dropdown>

            <TextArea
              label="Tell us more about your situation"
              placeholder="e.g., Dental surgery, School admission fees"
              necessityIndicator={isDescriptionRequired ? 'required' : 'optional'}
              value={formData.description}
              onChange={({ value }) => setFieldValue('description', value ?? '')}
            />

            {hasExistingFile ? (
              <Box display="flex" flexDirection="column" gap="spacing.2">
                <Text size="small" weight="medium" color="surface.text.gray.muted">
                  Supporting Documents
                </Text>
                <Box
                  display="flex"
                  justifyContent="space-between"
                  alignItems="center"
                  padding="spacing.4"
                  borderWidth="thin"
                  borderRadius="medium"
                  borderColor="surface.border.gray.muted">
                  <Link
                    icon={DownloadIcon}
                    iconPosition="left"
                    size="small"
                    onClick={() => downloadDocumentMutation.mutate(formData.documentFileId!)}
                    isDisabled={downloadDocumentMutation.isLoading}>
                    {downloadDocumentMutation.isLoading ? 'Loading…' : 'View Attachment'}
                  </Link>
                  <Button
                    variant="secondary"
                    color="negative"
                    size="xsmall"
                    icon={TrashIcon}
                    onClick={() => setFieldValue('documentFileId', null)}>
                    Remove
                  </Button>
                </Box>
              </Box>
            ) : (
              <FileUpload
                uploadType="single"
                label="Supporting Documents"
                helpText="Upload PDF, JPG, or PNG. Include any documents that support your request."
                accept={`${MimeType.PDF},.jpg,.jpeg,.png`}
                fileList={formData.documentFile ?? []}
                onChange={handleFileChange}
                onRemove={handleFileRemove}
                isDisabled={uploadDocumentMutation.isLoading}
              />
            )}

            {formData.isDocumentRequired && uploadFailed && (
              <Alert
                color="negative"
                isDismissible={false}
                title="Upload failed"
                description="Document upload failed. This category requires a document — please try uploading again before proceeding."
              />
            )}

            {!formData.isDocumentRequired && uploadFailed && hasFileSelected && (
              <Alert
                color="notice"
                isDismissible={false}
                title="Upload failed"
                description="Document upload failed. Since this document is optional, you can proceed without it or try uploading again."
              />
            )}
          </Box>
        </StepContentWrapper>

        <StepActionsWrapper width="462px">
          <Button variant="tertiary" onClick={closeWizard}>
            Close
          </Button>
          <Button type="submit" isDisabled={isNextDisabled}>
            Next
          </Button>
        </StepActionsWrapper>
      </FormWrapper>

      {formData.policySummary && formData.policySummary.length > 0 && (
        <PolicySummary
          policyFileId={formData.policyFileId ?? ''}
          policySummary={formData.policySummary}
        />
      )}
    </Box>
  );
};

export default RequestDetails;
